interface TeacherInputRequest {
  id: string;
  caseId: string;
  tipoInsumo: string;
  descripcionSolicitud: string;
  fechaLimite: string;
  estado: string;
  studentName: string;
  studentCurso: string;
  submittedAt?: string | null;
}

interface Props {
  request: TeacherInputRequest;
  onClick?: (id: string) => void;
  className?: string;
  compact?: boolean;
}

function daysBetween(d1: string, d2: string): number {
  return Math.ceil((new Date(d2).getTime() - new Date(d1).getTime()) / (1000 * 60 * 60 * 24));
}

const ESTADO_CONFIG: Record<string, { label: string; color: string; bg: string; dot: string; icon: string }> = {
  pendiente: { label: "Pendiente", color: "text-amber-700", bg: "bg-amber-50", dot: "bg-amber-400", icon: "ri-time-line text-amber-500" },
  solicitado: { label: "Solicitado", color: "text-amber-700", bg: "bg-amber-50", dot: "bg-amber-400", icon: "ri-time-line text-amber-500" },
  en_desarrollo: { label: "En desarrollo", color: "text-accent-700", bg: "bg-accent-50", dot: "bg-accent-400", icon: "ri-pen-nib-line text-accent-500" },
  entregado: { label: "Enviado", color: "text-primary-700", bg: "bg-primary-50", dot: "bg-primary-400", icon: "ri-check-line text-primary-500" },
  devuelto_correccion: { label: "Devuelto para corrección", color: "text-red-700", bg: "bg-red-50", dot: "bg-red-500 animate-pulse", icon: "ri-arrow-go-back-line text-red-500" },
  aprobado: { label: "Aprobado", color: "text-emerald-700", bg: "bg-emerald-50", dot: "bg-emerald-500", icon: "ri-verified-badge-line text-emerald-500" },
  vencido: { label: "Vencido", color: "text-red-700", bg: "bg-red-50", dot: "bg-red-500", icon: "ri-error-warning-line text-red-500" },
};

const AREA_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  convivencia: { label: "Convivencia Escolar", color: "text-primary-600", icon: "ri-shield-user-line" },
  orientacion: { label: "Orientación", color: "text-secondary-600", icon: "ri-compass-3-line" },
  psicologia: { label: "Psicología", color: "text-accent-600", icon: "ri-psychotherapy-line" },
  direccion: { label: "Dirección", color: "text-foreground-600", icon: "ri-building-line" },
  tribunal: { label: "Tribunal de Familia", color: "text-purple-600", icon: "ri-scales-3-line" },
};

function inferArea(tipoInsumo: string, descripcion: string): string {
  const t = (tipoInsumo + " " + descripcion).toLowerCase();
  if (t.includes("tribunal") || t.includes("judicial")) return "tribunal";
  if (t.includes("convivencia")) return "convivencia";
  if (t.includes("psicolog") || t.includes("psicoeducativo")) return "psicologia";
  if (t.includes("dirección") || t.includes("director")) return "direccion";
  return "orientacion";
}

export default function TeacherRequestCard({ request, onClick, className = "", compact = false }: Props) {
  const estadoConfig = ESTADO_CONFIG[request.estado] || ESTADO_CONFIG.pendiente;
  const todayStr = new Date().toISOString().split("T")[0];
  const d = request.estado === "vencido" ? -1 : daysBetween(todayStr, request.fechaLimite);
  const isUrgent = d <= 2 && (request.estado === "pendiente" || request.estado === "solicitado" || request.estado === "en_desarrollo");
  const area = inferArea(request.tipoInsumo, request.descripcionSolicitud);
  const areaConfig = AREA_CONFIG[area] || AREA_CONFIG.orientacion;
  const showAction = request.estado === "pendiente" || request.estado === "solicitado" || request.estado === "en_desarrollo" || request.estado === "devuelto_correccion" || request.estado === "vencido";

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border transition-all duration-200 ${
        isUrgent ? "border-red-200/60 bg-red-50/20" : request.estado === "devuelto_correccion" ? "border-orange-200/60 bg-orange-50/20" : "border-background-200/60 bg-white hover:bg-background-50/80"
      } ${onClick ? "cursor-pointer hover:border-background-300" : ""} ${className}`}
      onClick={() => onClick?.(request.id)}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`flex items-center justify-center w-2 h-2 rounded-full flex-shrink-0 ${estadoConfig.dot}`} />
          <p className="text-sm font-bold text-foreground-900 truncate">{request.tipoInsumo}</p>
          <span className={`badge-modern ${estadoConfig.bg} ${estadoConfig.color} border border-transparent`}>
            {estadoConfig.label}
          </span>
          {isUrgent && (
            <span className="badge-modern bg-red-100 text-red-700 border border-red-200/50">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-dot-pulse" />
              Urgente
            </span>
          )}
        </div>

        {!compact && (request.studentName) && (
          <div className="flex items-center gap-3 flex-wrap text-xs text-foreground-500 mt-1">
            <span className="flex items-center gap-1">
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-user-line text-foreground-400" />
              </span>
              {request.studentName}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex items-center justify-center w-4 h-4">
                <i className="ri-calendar-line text-foreground-400" />
              </span>
              {request.studentCurso}
            </span>
            <span className={`flex items-center gap-1 ${areaConfig.color}`}>
              <span className="flex items-center justify-center w-4 h-4">
                <i className={areaConfig.icon} />
              </span>
              {areaConfig.label}
            </span>
          </div>
        )}

        {!compact && (
          <p className="text-xs text-foreground-400 mt-1.5 line-clamp-2">{request.descripcionSolicitud}</p>
        )}
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="text-right">
          <p className={`text-xs font-medium ${isUrgent ? "text-red-600" : "text-foreground-500"}`}>
            {request.estado === "vencido" ? "Vencido: " : "Límite: "}{request.fechaLimite}
          </p>
          {request.estado === "vencido" ? (
            <p className="text-xs font-bold text-red-500">Plazo vencido</p>
          ) : request.estado === "aprobado" || request.estado === "entregado" ? (
            <p className="text-xs text-emerald-500 font-medium">
              {request.estado === "aprobado" ? "Aprobado" : "Enviado"} {request.submittedAt ? `el ${request.submittedAt}` : ""}
            </p>
          ) : (
            <p className={`text-xs font-bold ${isUrgent ? "text-red-500" : d <= 0 ? "text-red-500" : d <= 3 ? "text-amber-600" : "text-foreground-400"}`}>
              {d <= 0 ? `Vencido (${Math.abs(d)}d)` : `${d}d restantes`}
            </p>
          )}
        </div>

        {showAction && (
          <button
            className={`btn-modern-primary px-4 py-2 text-sm whitespace-nowrap ${
              request.estado === "devuelto_correccion" ? "!bg-orange-500 hover:!bg-orange-600" : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(request.id);
            }}
          >
            {request.estado === "devuelto_correccion" ? (
              <span className="flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-edit-line" />
                </span>
                Corregir
              </span>
            ) : request.estado === "en_desarrollo" ? (
              <span className="flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-pen-nib-line" />
                </span>
                Continuar
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-file-text-line" />
                </span>
                Completar informe guiado
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export { ESTADO_CONFIG, AREA_CONFIG, inferArea };
export type { TeacherInputRequest };